import { useEffect, useState } from "react";
import axios from "axios"; 
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../Store/apiStore";
import Loader2 from "../Component/Loader2/Loader2";
/*
 * `fallback` (opt-in): token verify hone tak kya dikhe. Unset → pehle jaisa
 * Loader2 (teen dots). Jis page ka apna loader hai (jaise /leads ka shimmer)
 * wo `fallback={null}` de sakta hai, taaki do alag loader ek ke baad ek na
 * dikhen. Sirf dikhne wali cheez badalti hai — verify ka logic wahi hai.
 *
 * `optimistic` (opt-in): token maujood ho to verify ka intezaar kiye bina page
 * turant dikha do — header, footer, cache wale cards sab. Verify peeche chalta
 * rehta hai; token galat nikle to neeche wala redirect pehle jaisa hi chalta
 * hai. Data phir bhi surakshit hai: har asli API call pe backend khud auth
 * check karta hai. Ye sirf lautne par dikhne wala "reload" hatata hai.
 */
const SecureRoute = ({ children, fallback, optimistic = false }) => {
  const [isValid, setIsValid] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  let token = localStorage.getItem("token");
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get("token");
    const urlAgentId = urlParams.get("agentId");
    if (urlToken) {
      token = urlToken;
      localStorage.setItem("token", urlToken);
      localStorage.setItem("onboardComplete", "true");
    }
    if (urlAgentId) {
      sessionStorage.setItem("SelectAgentId", urlAgentId);
    }
  }

  // `onboardComplete` / `onboardAgentComplete` reads removed with the guard
  // below — nothing in this component consumed them afterwards. The flag is
  // still WRITTEN here (line ~20, email-token path) and read by other screens,
  // so its contract is unchanged.
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/endusers/verifyToken`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        // Only log the userPUSH CODE  out when the SERVER explicitly says the token is
        // invalid. If the response is unclear (e.g. proxy HTML), keep them in.
        setIsValid(res?.data?.valid === false ? false : true);
      } catch (err) {
        // Network error / aborted request — very common on tunnels and during
        // page navigation on iOS Safari. A transient failure must NOT log the
        // user out; the backend still enforces auth on every real API call.
        setIsValid(true);
      }
    };
    if (token) {
      verifyToken();
    } else {
      setIsValid(false);
    }
  }, [token]);

  // This useEffect handles redirect when token is invalid
  useEffect(() => {
    if (isValid === false) {
      // Preserve the integration OAuth params across the wipe — they're NOT user
      // data (provider callback URL + CSRF state + platform). Without this, a
      // "Use a different account" / token-invalid clear here wipes them, so the
      // user can never be returned to Make/Zapier and the popup hangs forever.
      const oauthRedirectUri = localStorage.getItem("oauth_redirect_uri");
      const oauthState = localStorage.getItem("oauth_state");
      const oauthPlatform = localStorage.getItem("oauth_platform");
      // Per-window Make popup tag — only the Make consent popup has it; a normal
      // logged-out tab reads null and keeps nothing, so it can never leak here.
      const makePopupTag = sessionStorage.getItem("make_oauth_popup");

      localStorage.clear();
      sessionStorage.clear();

      if (oauthRedirectUri)
        localStorage.setItem("oauth_redirect_uri", oauthRedirectUri);
      if (oauthState) localStorage.setItem("oauth_state", oauthState);
      if (oauthPlatform) localStorage.setItem("oauth_platform", oauthPlatform);
      if (makePopupTag)
        sessionStorage.setItem("make_oauth_popup", makePopupTag);

      // Preserve a deep-link destination (e.g. the email/push nudge target) so
      // the user is returned here AFTER they log in. Stored in localStorage —
      // login only clears sessionStorage, so this survives the auth round-trip.
      const intended = location.pathname + location.search;
      localStorage.clear();
      sessionStorage.clear();
      if (oauthRedirectUri)
        localStorage.setItem("oauth_redirect_uri", oauthRedirectUri);
      if (oauthState) localStorage.setItem("oauth_state", oauthState);
      if (oauthPlatform) localStorage.setItem("oauth_platform", oauthPlatform);
      if (makePopupTag)
        sessionStorage.setItem("make_oauth_popup", makePopupTag);
      if (location.pathname === "/recommended-plan") {
        localStorage.setItem("postLoginRedirect", intended);
      }
      // window.location.replace = "/?step=3";
      navigate("/?step=3", { replace: true });
    }
  }, [isValid]);
  if (isValid === null) {
    if (optimistic && token) {
      // Neeche ke do redirect (login ke baad ka deep-link, AppSumo redeem)
      // verify ke baad hi chalte hain — wo lagne wale hon to page pehle se mat
      // dikhao, warna wo ek pal dikh kar gayab ho jaata.
      let redirectPending = false;
      try {
        redirectPending =
          !!localStorage.getItem("postLoginRedirect") ||
          localStorage.getItem("appsumoRedeemPending") === "true";
      } catch (_) {
        /* storage blocked — normal fallback */
        redirectPending = true;
      }
      if (!redirectPending) return children;
    }
    return fallback === undefined ? <Loader2 /> : fallback;
  }

  // After login, send the user back to the deep-link they originally tapped
  // (saved above). Runs once, then clears the flag to avoid loops.
  if (isValid === true) {
    const redirect = localStorage.getItem("postLoginRedirect");
    if (redirect && location.pathname + location.search !== redirect) {
      localStorage.removeItem("postLoginRedirect");
      return <Navigate to={redirect} replace />;
    }
  }

  /*
   * [FIX: appsumo-redeem-resume] An AppSumo signup that never finished redeeming
   * belongs on the redeem screen, whatever protected route it lands on.
   *
   * `appsumoRedeemPending` is set when an AppSumo user verifies their OTP and is
   * cleared ONLY when they redeem or skip (AppSumoRedeemPage), so while it is
   * still set the code is genuinely un-redeemed.
   *
   * The guard lives HERE, not on the individual routes, because the user can
   * arrive at any of them without passing through the OTP handler that used to
   * make this decision: reopening the app on a stored token, the onboarding
   * resume route (rexpt_onboarding_resume_route → /business-step), a deep link,
   * or a plain refresh. Each of those skipped the redeem step entirely.
   *
   * /appsumo-redeem itself is excluded or this would loop.
   */
  if (isValid === true && location.pathname !== "/appsumo-redeem") {
    let redeemPending = false;
    try {
      redeemPending =
        localStorage.getItem("appsumoRedeemPending") === "true";
    } catch (_) {
      /* storage blocked — fall through, the user just isn't redirected */
    }
    if (redeemPending) {
      return <Navigate to="/appsumo-redeem" replace />;
    }
  }
  /*
   * REMOVED: the `onboardComplete !== 'true'` → /business-step guard.
   *
   * It bounced /fast-agent-detail to /business-step whenever that localStorage
   * flag was not exactly the string "true" — which is why opening the dashboard
   * in a NEW TAB flashed /business-step before landing back on
   * /fast-agent-detail. The flag is written from ~10 places, and at least one
   * (`SignUp.js:239`) stores `user?.verifyDetails` unguarded, so a boolean or
   * undefined is coerced to "false" / "undefined" and the guard fires for a
   * user who HAS finished onboarding.
   *
   * FastAgentDetails already resolves this properly on mount — it probes the
   * real agent count, distinguishes an abandoned draft from a brand-new user,
   * and routes accordingly. This guard was a blunt duplicate of that decision,
   * made from a flag instead of from data, and it raced the real one.
   */
  if (isValid === true) {
    return children;
  }

  // While redirecting, return null
  return null;
};
export default SecureRoute;
